import React from 'react'
import { SUB_EVENT_QUERY_KEYS } from '@/api/queryKeys'
import { InfiniteSelect, type SelectOption } from '../InfiniteSelect'
import { fetchSubEvents } from '@/api/subEventApi'

export interface SubEventSelectValue {
  id: string
  name: string
}

interface SubEventSelectProps {
  value?: SubEventSelectValue | SubEventSelectValue[] | null
  onValueChange: (
    event: SubEventSelectValue | SubEventSelectValue[] | null
  ) => void
  placeholder?: string
  label?: string
  required?: boolean
  className?: string
  error?: string
  disabled?: boolean
  statusFilter?: string[]
  categoryFilter?: string[]
  multiple?: boolean
  maxSelections?: number
  variant?: 'default' | 'compact'
  enableSearch?: boolean
}

export function SubEventSelect({
  value,
  onValueChange,
  placeholder = 'Select subevent...',
  label = 'Sub Event',
  required = false,
  className = '',
  error,
  disabled = false,
  statusFilter,
  categoryFilter,
  multiple = false,
  maxSelections,
  variant = 'default',
  enableSearch = true,
}: Readonly<SubEventSelectProps>) {
  const transformSubEvent = (event: SubEventSelectValue): SelectOption => ({
    id: event.id,
    label: event.name,
    value: event.id,
    eventData: event,
  })

  const selectedOptions = React.useMemo(() => {
    if (multiple) {
      return Array.isArray(value) ? value.map(transformSubEvent) : []
    } else {
      return value && !Array.isArray(value) ? transformSubEvent(value) : null
    }
  }, [value, multiple])

  const handleValueChange = (options: SelectOption | SelectOption[] | null) => {
    if (!options) {
      onValueChange(null)
      return
    }

    if (multiple && Array.isArray(options)) {
      const events: SubEventSelectValue[] = options
        .filter(Boolean)
        .map(option => option.eventData as SubEventSelectValue)

      onValueChange(events.length > 0 ? events : null)
    } else if (!multiple && !Array.isArray(options)) {
      const option = options

      if (option.eventData && typeof option.eventData === 'object') {
        onValueChange(option.eventData as SubEventSelectValue)
      } else {
        const reconstructedEvent: SubEventSelectValue = {
          id: option.id,
          name: option.label,
        }
        onValueChange(reconstructedEvent)
      }
    }
  }

  const additionalParams = React.useMemo(() => {
    const params: Record<string, unknown> = {
      fetchNamesOnly: true,
    }

    if (statusFilter && statusFilter.length > 0) {
      params.status = statusFilter
    }

    if (categoryFilter && categoryFilter.length > 0) {
      params.categories = categoryFilter
    }

    return params
  }, [statusFilter, categoryFilter])

  const resolvedPlaceholder =
    multiple && placeholder === 'Select sub event...'
      ? 'Select sub events...'
      : placeholder

  const compactClassName =
    variant === 'compact'
      ? 'w-56 h-9 border-ss-neutral-200 bg-transparent hover:border-ss-blue-700 focus:border-ss-blue-700'
      : className

  return (
    <InfiniteSelect<SubEventSelectValue>
      queryKey={[...SUB_EVENT_QUERY_KEYS.allSubEvents, 'names-only']}
      queryFn={fetchSubEvents}
      transformData={transformSubEvent}
      value={selectedOptions}
      onValueChange={handleValueChange}
      placeholder={resolvedPlaceholder}
      label={variant === 'compact' ? undefined : label}
      required={required}
      className={compactClassName}
      error={error}
      disabled={disabled}
      searchPlaceholder="Search sub events..."
      emptyMessage="No sub events available"
      noResultsMessage="No sub events found"
      pageSize={20}
      additionalParams={additionalParams}
      enableSearch={enableSearch}
      showAvatar={false}
      multiple={multiple}
      maxSelections={maxSelections}
    />
  )
}
